/** @jsxImportSource preact */
import { useState, useEffect } from 'preact/hooks'
import { config } from '../config'
import { useFrontContext, extractBookingId } from '../providers/front'

type FormAction = 'get-emails' | 'get-notes'
type FormStatus = 'idle' | 'loading' | 'success' | 'error'

type Note = {
  id: string
  content: string
  add_time?: string
  addTime?: string
}

export function General() {
  const [bookingId, setBookingId] = useState('')
  const [selectedAction, setSelectedAction] = useState<FormAction>('get-emails')
  const [formStatus, setFormStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [emails, setEmails] = useState<string[]>([])
  const [notes, setNotes] = useState<Note[]>([])
  const frontContext = useFrontContext()

  // Effect to extract booking ID from Front context
  useEffect(() => {
    if (frontContext) {
      const extractedBookingId = extractBookingId(frontContext)
      if (extractedBookingId) {
        setBookingId(extractedBookingId)
      } else {
        setBookingId('')
      }
    }
  }, [frontContext])

  const handleSubmit = async (event: Event) => {
    event.preventDefault()

    if (!bookingId) {
      setErrorMessage('Booking ID is required')
      return
    }

    setFormStatus('loading')
    setErrorMessage('')

    try {
      if (selectedAction === 'get-emails') {
        await fetchEmails()
      } else if (selectedAction === 'get-notes') {
        await fetchNotes()
      }

      setFormStatus('success')
    } catch (error) {
      console.error('Error:', error)
      setFormStatus('error')
      setErrorMessage('An error occurred. Please try again.')
    }
  }

  const sanitizeHtml = (html: string): string => {
    // Create a temporary div element
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html

    // Remove potentially dangerous elements
    const scripts = tempDiv.querySelectorAll('script')
    scripts.forEach(script => script.remove())

    // Remove on* attributes from all elements
    const allElements = tempDiv.querySelectorAll('*')
    allElements.forEach(el => {
      const attributes = Array.from(el.attributes)
      attributes.forEach(attr => {
        if (attr.name.toLowerCase().startsWith('on') || attr.value.toLowerCase().includes('javascript:')) {
          el.removeAttribute(attr.name)
        }
      })
    })

    // Remove iframe elements
    const iframes = tempDiv.querySelectorAll('iframe')
    iframes.forEach(iframe => iframe.remove())

    return tempDiv.innerHTML
  }

  const fetchEmails = async () => {
    const response = await fetch(`${config.apiBaseUrl}/bookings/${bookingId.trim()}/emails`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Error fetching emails: ${response.statusText}`)
    }

    const data = await response.json()
    setEmails(data)
  }

  const fetchNotes = async () => {
    const response = await fetch(`${config.apiBaseUrl}/bookings/${bookingId}/notes`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Error fetching notes: ${response.statusText}`)
    }

    const data = await response.json()
    setNotes(data)
  }

  const renderEmails = () => {
    if (emails.length === 0 && formStatus === 'success') {
      return <p className="text-gray-600 mt-4">No emails found for this booking.</p>
    }

    return (
      <div className="mt-6 space-y-6">
        {emails.map(email => {
          return (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(email as string) }}
              />
            </div>
          )
        })}
      </div>
    )
  }

  const renderNotes = () => {
    if (notes.length === 0 && formStatus === 'success') {
      return <p className="text-gray-600 mt-4">No notes found for this booking.</p>
    }

    return (
      <div className="mt-6 space-y-4">
        {notes.map(note => (
          <div
            key={note.id}
            className="bg-gray-50 p-4 rounded-lg border border-gray-200"
          >
            <div className="text-sm text-gray-600 mb-1">
              {new Date(note.addTime || note.add_time || '').toLocaleString()}
            </div>
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(note.content) }}
            />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <style>
        {`
          .prose a { color: #4f46e5; text-decoration: underline; }
          .prose hr { margin: 1rem 0; border-color: #e5e7eb; }
          .prose h3 { font-size: 1.125rem; font-weight: 600; margin: 1rem 0 0.5rem 0; }
          .prose p { margin-bottom: 0.75rem; }
        `}
      </style>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">General Booking Information</h1>

      <form
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <div>
          <label
            htmlFor="bookingId"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Booking ID
          </label>
          <input
            type="text"
            id="bookingId"
            value={bookingId}
            onChange={e => setBookingId((e.target as HTMLInputElement).value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            required
            placeholder="Enter booking ID"
            disabled={false}
          />
        </div>

        <div>
          <label
            htmlFor="action"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Action
          </label>
          <select
            id="action"
            value={selectedAction}
            onChange={e => setSelectedAction((e.target as HTMLSelectElement).value as FormAction)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="get-emails">Retrieve Emails</option>
            <option value="get-notes">Retrieve Notes</option>
          </select>
        </div>

        <div>
          <button
            type="submit"
            disabled={formStatus === 'loading'}
            className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {formStatus === 'loading'
              ? 'Loading...'
              : selectedAction === 'get-emails'
                ? 'Retrieve Emails'
                : 'Retrieve Notes'}
          </button>
        </div>

        {errorMessage && <div className="p-3 bg-red-50 text-red-700 rounded-md">{errorMessage}</div>}

        {formStatus === 'success' && (
          <div className="p-3 bg-green-50 text-green-700 rounded-md">
            Successfully retrieved {selectedAction === 'get-emails' ? 'emails' : 'notes'}.
          </div>
        )}
      </form>

      {selectedAction === 'get-emails' && formStatus === 'success' && renderEmails()}
      {selectedAction === 'get-notes' && formStatus === 'success' && renderNotes()}

      <div className="mt-8 pt-4 border-t border-gray-200">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800"
        >
          Back to Dashboard
        </a>
      </div>
    </div>
  )
}
